import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../api';

type ReferralOverview = {
  referrals: any[];
  rewards: any[];
  auditEvents: any[];
  auditPagination: { page: number; limit: number; total: number; pages: number };
  metrics: { referrals: number; rewards: Record<string, number> };
};

const adminRequestConfig = () => {
  const token = localStorage.getItem('admin_auth_token');
  return token ? { headers: { 'x-auth-token': token } } : undefined;
};

const displayName = (value: any) => value?.name || value?.email || 'Unknown user';
const formatDate = (value?: string) => value ? new Date(value).toLocaleDateString() : '—';

const AdminReferralTab: React.FC = () => {
  const [overview, setOverview] = useState<ReferralOverview>({
    referrals: [],
    rewards: [],
    auditEvents: [],
    auditPagination: { page: 1, limit: 50, total: 0, pages: 0 },
    metrics: { referrals: 0, rewards: {} },
  });
  const [email, setEmail] = useState('');
  const [percent, setPercent] = useState(5);
  const [validityDays, setValidityDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/admin/referrals?page=${overview.auditPagination.page || 1}`, adminRequestConfig());
      setOverview(response.data?.data || {
        referrals: [],
        rewards: [],
        auditEvents: [],
        auditPagination: { page: 1, limit: 50, total: 0, pages: 0 },
        metrics: { referrals: 0, rewards: {} },
      });
      setError(null);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to load referral records.');
    } finally {
      setLoading(false);
    }
  }, [overview.auditPagination.page]);

  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  const configureWelcomeDiscount = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await api.patch('/users/admin/welcome-discount', { email, percent, validityDays }, adminRequestConfig());
      setSuccess(`Welcome discount configured for ${email.trim()}.`);
      setEmail('');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to configure welcome discount.');
    } finally {
      setSaving(false);
    }
  };

  const voidReward = async (rewardId: string) => {
    const reason = window.prompt('Provide a reason for voiding this reward:')?.trim();
    if (!reason) return;
    try {
      setSaving(true);
      setError(null);
      await api.post(`/users/admin/referral-rewards/${rewardId}/void`, { reason }, adminRequestConfig());
      setSuccess('Referral reward voided.');
      await fetchOverview();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to void this reward.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5">Referral Management</Typography>
      <Typography color="text.secondary">
        Monitor referrals and rewards, and configure a referred student&apos;s welcome discount.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 2, py: 1.5 }}><Typography variant="caption">Structured referrals</Typography><Typography variant="h6">{overview.metrics.referrals}</Typography></Paper>
        {Object.entries(overview.metrics.rewards).map(([status, count]) => (
          <Paper key={status} sx={{ px: 2, py: 1.5 }}><Typography variant="caption">{status} rewards</Typography><Typography variant="h6">{count}</Typography></Paper>
        ))}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Configure a student welcome discount</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField label="Student email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          <TextField label="Discount (%)" type="number" value={percent} onChange={(event) => setPercent(Number(event.target.value))} inputProps={{ min: 0, max: 100 }} />
          <TextField label="Validity (days)" type="number" value={validityDays} onChange={(event) => setValidityDays(Number(event.target.value))} inputProps={{ min: 1, max: 365 }} />
          <Button variant="contained" onClick={configureWelcomeDiscount} disabled={saving || !email.trim()}>
            Save discount
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, overflowX: 'auto' }}>
        <Typography variant="h6" gutterBottom>Recent referrals</Typography>
        {loading ? <CircularProgress /> : overview.referrals.length === 0 ? (
          <Typography color="text.secondary">No referral records are available yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Referrer</TableCell><TableCell>Referred student</TableCell><TableCell>Code</TableCell>
              <TableCell>Status</TableCell><TableCell>Distinct features</TableCell><TableCell>Milestones</TableCell><TableCell>Registered</TableCell>
            </TableRow></TableHead>
            <TableBody>{overview.referrals.map((referral) => (
              <TableRow key={referral._id}>
                <TableCell>{displayName(referral.referrerId)}</TableCell>
                <TableCell>{displayName(referral.referredUserId)}</TableCell>
                <TableCell>{referral.referralCode}</TableCell>
                <TableCell>{referral.status}</TableCell>
                <TableCell>{referral.distinctPaidFeatures?.length || 0}</TableCell>
                <TableCell>{referral.milestonesReached?.join(', ') || '—'}</TableCell>
                <TableCell>{formatDate(referral.registeredAt)}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Paper>

      <Paper sx={{ p: 3, overflowX: 'auto' }}>
        <Typography variant="h6" gutterBottom>Referral rewards</Typography>
        {!loading && overview.rewards.length === 0 ? (
          <Typography color="text.secondary">No referral rewards have been issued yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Referrer</TableCell><TableCell>Referred student</TableCell><TableCell>Milestone</TableCell>
              <TableCell>Discount</TableCell><TableCell>Status</TableCell><TableCell>Expires</TableCell><TableCell>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>{overview.rewards.map((reward) => (
              <TableRow key={reward._id}>
                <TableCell>{displayName(reward.referrerId)}</TableCell>
                <TableCell>{displayName(reward.referredUserId)}</TableCell>
                <TableCell>{reward.milestone}</TableCell>
                <TableCell>{reward.discountPercent}%</TableCell>
                <TableCell>{reward.status}</TableCell>
                <TableCell>{formatDate(reward.expiresAt)}</TableCell>
                <TableCell>
                  {['AVAILABLE', 'RESERVED'].includes(reward.status) && (
                    <Button color="error" size="small" disabled={saving} onClick={() => voidReward(reward._id)}>Void</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Paper>

      <Paper sx={{ p: 3, overflowX: 'auto' }}>
        <Typography variant="h6" gutterBottom>Referral audit history</Typography>
        {!loading && overview.auditEvents.length === 0 ? (
          <Typography color="text.secondary">No referral audit events have been recorded yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Time</TableCell><TableCell>Event</TableCell><TableCell>Actor</TableCell>
              <TableCell>Student / referrer</TableCell><TableCell>Payment reference</TableCell><TableCell>Details</TableCell>
            </TableRow></TableHead>
            <TableBody>{overview.auditEvents.map((event) => (
              <TableRow key={event._id}>
                <TableCell>{formatDate(event.createdAt)}</TableCell>
                <TableCell>{event.eventType.replaceAll('_', ' ')}</TableCell>
                <TableCell>{displayName(event.actorUserId)}</TableCell>
                <TableCell>{displayName(event.affectedUserId)} / {displayName(event.referrerId)}</TableCell>
                <TableCell>{event.paymentReference || '—'}</TableCell>
                <TableCell>{Object.entries(event.details || {}).map(([key, value]) => `${key}: ${String(value)}`).join(' · ') || '—'}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            disabled={loading || overview.auditPagination.page <= 1}
            onClick={() => setOverview((current) => ({
              ...current,
              auditPagination: { ...current.auditPagination, page: current.auditPagination.page - 1 },
            }))}
          >
            Previous
          </Button>
          <Typography variant="body2">
            Page {overview.auditPagination.page} of {overview.auditPagination.pages || 1} ({overview.auditPagination.total} events)
          </Typography>
          <Button
            disabled={loading || overview.auditPagination.page >= overview.auditPagination.pages}
            onClick={() => setOverview((current) => ({
              ...current,
              auditPagination: { ...current.auditPagination, page: current.auditPagination.page + 1 },
            }))}
          >
            Next
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminReferralTab;
